import head from 'lodash/head';
import moment from 'moment';
import React from 'react';
import { Controller, useFormContext, UseFormReturn } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';

import {
  ADDITIONAL_INFORMATION_TYPES,
  MessageType,
} from '@savgroup-front-common/types';

import { CommonAttachmentService } from '../../api';
import Checkbox from '../../atoms/checkbox/Checkbox';
import { FloatInput, FormGroup, LegacyDatepicker } from '../../atoms/Form';
import { FieldMessages } from '../../atoms/Form/common/helpers/getFinalFieldState.types';
import { PriceInput } from '../../atoms/Form/PriceInput';
import { Tooltip } from '../../atoms/Tooltip';
import { ActionCreators as fileUploadActionCreators } from '../../domains/fileUpload';
import { hasFailureResponse } from '../../helpers';
import {
  InputHookForm,
  NumberInputHookForm,
  TextareaHookForm,
} from '../../molecules/Form';
import { InformationIcon } from '../../protons/icons';

import { useAdditionalInformationContext } from './AdditionalInformation.context';
import { $ToolTipContainer } from './AdditionalInformation.styles';
import { AdditionalInformationDidactics } from './AdditionalInformationDidactics/AdditionalInformationDidactics';
import AdditionalInformationEnum from './AdditionalInformationEnum';
import AdditionalInformationFileInput from './AdditionalInformationFileInput';
import { buildInputName } from './AdditionalInformationInput.helpers';
import { $ToolTipFormatter } from './AdditionalInformationInput.styles';
import { AdditionalInformationMultiFileInput } from './AdditionalInformationMultiFileInput/AdditionalInformationMultiFileInput';
import { AdditionalInformationSerialNumber } from './AdditionalInformationSerialNumber/AdditionalInformationSerialNumber';
import { AdditionalInformation } from './hooks/useAdditionalInformation';

interface AdditionalInformationInputProps {
  label?: MessageType | string;
  placeholder?: string | undefined;
  formContext?: UseFormReturn & { warnings?: FieldMessages };

  neededInformationItem: AdditionalInformation;
}

export const AdditionalInformationInput: React.FC<
  AdditionalInformationInputProps
> = ({
  label = undefined,
  placeholder = undefined,
  formContext = undefined,
  neededInformationItem,
}) => {
  const {
    isRequired,
    importByQrCode,
    validatingRegex,
    claimIds,
    internalId,
    description,
    id,
    documentType,
    type,
    fileUploadEndpoints = {},
    possibleValues,
  } = neededInformationItem;

  const {
    prefixFormName,
    isLiveUpload,
    isNewDesign,
    isScrollIntoView,
    suffixOptionButton,
    currency,
    onAdditionalInformationSave,
  } = useAdditionalInformationContext();
  const defaultContext = useFormContext();

  const theme = useTheme();

  const {
    control,
    formState: { errors },
    register,
    setValue,
    trigger,
  } = formContext || defaultContext;
  const warnings = formContext?.warnings || {};

  const dispatch = useDispatch();
  const name = buildInputName({ prefixFormName, name: internalId });

  const postLabel = description && (
    <$ToolTipContainer>
      <Tooltip content={<$ToolTipFormatter>{description}</$ToolTipFormatter>}>
        <InformationIcon color={theme.colors.primary} size="20px" />
      </Tooltip>
    </$ToolTipContainer>
  );

  switch (type) {
    case ADDITIONAL_INFORMATION_TYPES.SUPPLIER_CREDIT_NOTE_REJECTION_TYPE:
    case ADDITIONAL_INFORMATION_TYPES.IRIS_SYMPTOM_CODE:
    case ADDITIONAL_INFORMATION_TYPES.IRIS_SECTION_CODE:
    case ADDITIONAL_INFORMATION_TYPES.IRIS_REPAIR_CODE:
    case ADDITIONAL_INFORMATION_TYPES.IRIS_EXTENDED_CONDITION_CODE:
    case ADDITIONAL_INFORMATION_TYPES.IRIS_DEFECT_CODE:
    case ADDITIONAL_INFORMATION_TYPES.IRIS_CONDITION_CODE:
    case ADDITIONAL_INFORMATION_TYPES.ENUM: {
      return (
        <FormGroup>
          <AdditionalInformationEnum
            id={id}
            name={name}
            label={label}
            isRequired={isRequired}
            postLabel={postLabel}
            placeholder={placeholder}
            possibleValues={possibleValues}
            formContext={formContext}
            onAdditionalInformationSave={onAdditionalInformationSave}
            isScrollIntoView={isScrollIntoView}
            suffixOptionButton={suffixOptionButton}
            isLabelMultiLineAllowed
          />
        </FormGroup>
      );
    }

    case ADDITIONAL_INFORMATION_TYPES.SUPPLIER_CREDIT_NOTE_AMOUNT_EXCL_VAT:
    case ADDITIONAL_INFORMATION_TYPES.PRICE: {
      return (
        <FormGroup>
          <Controller
            name={name}
            control={control}
            render={({ field }) => {
              return (
                <PriceInput
                  label={label}
                  name={field.name}
                  errors={errors}
                  value={field.value}
                  currency={currency}
                  onChange={field.onChange}
                  isRequired={isRequired}
                />
              );
            }}
          />
        </FormGroup>
      );
    }

    case ADDITIONAL_INFORMATION_TYPES.DATE: {
      return (
        <FormGroup>
          <Controller
            control={control}
            name={name}
            render={({ field }) => (
              <LegacyDatepicker
                ref={field.ref}
                errors={errors}
                warnings={warnings}
                isRequired={isRequired}
                placeholder={placeholder}
                label={label}
                name={field.name}
                postLabel={postLabel}
                onChange={field.onChange}
                value={moment(field.value)}
                onBlur={async () => {
                  field.onBlur();
                  await onAdditionalInformationSave();
                }}
                dataTestId={`additional-information-input-datepicker-${id}`}
              />
            )}
            defaultValue={null}
          />
        </FormGroup>
      );
    }
    case ADDITIONAL_INFORMATION_TYPES.INTEGER: {
      return (
        <FormGroup>
          <NumberInputHookForm
            internalId={id}
            errors={errors}
            warnings={warnings}
            name={name}
            isRequired={isRequired}
            placeholder={placeholder}
            label={label}
            postLabel={postLabel}
            onBlur={async () => {
              await onAdditionalInformationSave();
            }}
            register={register}
            dataTestId={`additional-information-input-integer-${id}`}
            isScrollIntoView={isScrollIntoView}
            isLabelMultiLineAllowed
          />
        </FormGroup>
      );
    }
    case ADDITIONAL_INFORMATION_TYPES.DECIMAL: {
      return (
        <FormGroup>
          <Controller
            name={name}
            control={control}
            render={({ field }) => {
              return (
                <FloatInput
                  internalId={id}
                  ref={field.ref}
                  errors={errors}
                  warnings={warnings}
                  name={name}
                  isRequired={isRequired}
                  placeholder={placeholder}
                  label={label}
                  postLabel={postLabel}
                  onBlur={async () => {
                    await onAdditionalInformationSave();
                  }}
                  value={field.value}
                  onChange={field.onChange}
                  dataTestId={`additional-information-input-decimal-${id}`}
                />
              );
            }}
          />
        </FormGroup>
      );
    }
    case ADDITIONAL_INFORMATION_TYPES.IMEI: {
      return (
        <FormGroup>
          <InputHookForm
            internalId={id}
            errors={errors}
            warnings={warnings}
            name={name}
            isRequired={isRequired}
            placeholder={placeholder}
            label={label}
            postLabel={postLabel}
            dataTestId={`additional-information-input-imei-${id}`}
            onBlur={async () => {
              await onAdditionalInformationSave();
            }}
            register={register}
            isScrollIntoView={isScrollIntoView}
            isLabelMultiLineAllowed
          />
        </FormGroup>
      );
    }
    case ADDITIONAL_INFORMATION_TYPES.MULTI_FILES: {
      return (
        <FormGroup>
          <Controller
            control={control}
            name={name}
            defaultValue={null}
            render={({ field }) => {
              return (
                <AdditionalInformationMultiFileInput
                  name={name}
                  label={label}
                  postLabel={postLabel}
                  errors={errors}
                  isRequired={isRequired}
                  additionalInformationId={id}
                  claimIds={claimIds}
                  documentType={documentType}
                  onChange={async (files) => {
                    if (isLiveUpload) {
                      if (!('uploadEndpoint' in fileUploadEndpoints)) {
                        const responses = await Promise.all(
                          Object.values(fileUploadEndpoints).map(
                            (fileUploadEndpoint) => {
                              return CommonAttachmentService.uploadAttachmentWithProgress(
                                {
                                  files,
                                  endpoint: fileUploadEndpoint.uploadEndpoint,
                                  notify: ({ progress }) => {
                                    setValue(
                                      name,
                                      files?.map((file, index) => {
                                        return {
                                          ...(field.value[index] || {}),
                                          progress,
                                          value: file,
                                        };
                                      }),
                                    );

                                    trigger(name);
                                  },
                                },
                              );
                            },
                          ),
                        );

                        if (responses.some(hasFailureResponse)) {
                          const response = responses.find(hasFailureResponse);

                          setValue(
                            name,
                            files?.map((file, index) => {
                              return {
                                ...(field.value[index] || {}),
                                errors: response?.errors,
                                value: file,
                              };
                            }),
                          );

                          await trigger(name);
                        } else {
                          const response = responses[0];

                          setValue(
                            name,
                            files?.map((file, index) => {
                              return {
                                ...(field.value[index] || {}),
                                ...((response as any).value || {}),
                                progress: 100,
                                value: file,
                              };
                            }),
                          );
                        }
                      } else {
                        const response =
                          await CommonAttachmentService.uploadAttachmentWithProgress(
                            {
                              files,
                              endpoint:
                                fileUploadEndpoints.uploadEndpoint as unknown as string,
                              notify: ({ progress }) => {
                                setValue(
                                  name,
                                  files?.map((file, index) => {
                                    return {
                                      ...(field.value[index] || {}),
                                      progress,
                                      value: file,
                                    };
                                  }),
                                );
                              },
                            },
                          );

                        if (response.failure) {
                          setValue(
                            name,
                            files?.map((file, index) => {
                              return {
                                ...(field.value[index] || {}),
                                value: file,
                                errors: response.errors,
                              };
                            }),
                          );
                        } else {
                          setValue(
                            name,
                            files?.map((file, index) => {
                              return {
                                ...(field.value[index] || {}),
                                ...((response as any).value || {}),
                                progress: 100,
                                value: file,
                              };
                            }),
                          );
                        }

                        await trigger(name);
                      }

                      return;
                    }

                    const allFiles = (field.value || []).concat(
                      files?.map((file, index) => ({
                        ...(field.value?.at(index) || {}),
                        value: file,
                      })),
                    );

                    field.onChange(allFiles);
                  }}
                  onRemove={(_file, index) => {
                    if (field.value.errors) {
                      field.onChange(null);

                      return;
                    }

                    if (isLiveUpload) {
                      if (!('uploadEndpoint' in fileUploadEndpoints)) {
                        Object.values(fileUploadEndpoints).map(
                          (fileUploadEndpoint) => {
                            return dispatch(
                              fileUploadActionCreators.deleteFile({
                                endpoint: fileUploadEndpoint.deleteEndpoint,
                                indexer: id,
                                blobValue:
                                  field.value?.additionalInformationStringValue,
                              }),
                            );
                          },
                        );
                      } else {
                        dispatch(
                          fileUploadActionCreators.deleteFile({
                            endpoint: fileUploadEndpoints.deleteEndpoint,
                            indexer: id,
                            blobValue:
                              field.value?.additionalInformationStringValue,
                          }),
                        );
                      }
                    }

                    field.onChange(
                      field.value?.filter((_: any, i: number) => i !== index) ??
                        [],
                    );
                  }}
                  value={field.value}
                  isLiveUpload={isLiveUpload}
                  isNewDesign={isNewDesign}
                  dataTestId={`additional-information-input-fileupload-${id}`}
                  isImportByQrCode={importByQrCode}
                />
              );
            }}
          />
        </FormGroup>
      );
    }
    case ADDITIONAL_INFORMATION_TYPES.FILE: {
      return (
        <FormGroup>
          <Controller
            control={control}
            name={name}
            defaultValue={null}
            render={({ field }) => {
              return (
                <AdditionalInformationFileInput
                  name={name}
                  label={label}
                  postLabel={postLabel}
                  isRequired={isRequired}
                  additionalInformationId={id}
                  claimIds={claimIds}
                  documentType={documentType}
                  onChange={async (files) => {
                    const file = head(files);

                    if (files?.length !== 1) {
                      return;
                    }

                    if (isLiveUpload) {
                      if (!('uploadEndpoint' in fileUploadEndpoints)) {
                        const responses = await Promise.all(
                          Object.values(fileUploadEndpoints).map(
                            (fileUploadEndpoint) => {
                              return CommonAttachmentService.uploadAttachmentWithProgress(
                                {
                                  file,
                                  endpoint: fileUploadEndpoint.uploadEndpoint,
                                  notify: ({ progress }) => {
                                    setValue(name, {
                                      ...(field.value || {}),
                                      progress,
                                      value: file,
                                    });
                                    trigger(name);
                                  },
                                },
                              );
                            },
                          ),
                        );

                        if (responses.some(hasFailureResponse)) {
                          const response = responses.find(hasFailureResponse);

                          setValue(name, {
                            ...(field.value || {}),
                            errors: (response as any).errors,
                            value: file,
                          });
                          await trigger(name);
                        } else {
                          const response = responses[0];

                          setValue(name, {
                            ...(field.value || {}),
                            ...((response as any).value || {}),
                            progress: 100,
                            value: file,
                          });
                        }
                      } else {
                        const response =
                          await CommonAttachmentService.uploadAttachmentWithProgress(
                            {
                              file,
                              endpoint:
                                fileUploadEndpoints.uploadEndpoint as unknown as string,
                              notify: ({ progress }) => {
                                setValue(name, {
                                  ...(field.value || {}),
                                  progress,
                                  value: file,
                                });
                              },
                            },
                          );

                        if (response.failure) {
                          setValue(name, {
                            ...(field.value || {}),
                            value: file,
                            errors: response.errors,
                          });
                        } else {
                          setValue(name, {
                            ...(field.value || {}),
                            ...((response as any).value || {}),
                            progress: 100,
                            value: file,
                          });
                        }

                        await trigger(name);
                      }

                      return;
                    }

                    field.onChange({ ...(field.value || {}), value: file });
                  }}
                  onRemove={() => {
                    if (field.value.errors) {
                      field.onChange(null);

                      return;
                    }

                    if (!('uploadEndpoint' in fileUploadEndpoints)) {
                      Object.values(fileUploadEndpoints).map(
                        (fileUploadEndpoint) => {
                          return dispatch(
                            fileUploadActionCreators.deleteFile({
                              endpoint: fileUploadEndpoint.deleteEndpoint,
                              indexer: id,
                              blobValue:
                                field.value?.additionalInformationStringValue,
                            }),
                          );
                        },
                      );
                    } else {
                      dispatch(
                        fileUploadActionCreators.deleteFile({
                          endpoint: fileUploadEndpoints.deleteEndpoint,
                          indexer: id,
                          blobValue:
                            field.value?.additionalInformationStringValue,
                        }),
                      );
                    }

                    field.onChange(null);
                  }}
                  value={field.value}
                  isLiveUpload={isLiveUpload}
                  isNewDesign={isNewDesign}
                  dataTestId={`additional-information-input-fileupload-${id}`}
                  isImportByQrCode={importByQrCode}
                />
              );
            }}
          />
        </FormGroup>
      );
    }
    case ADDITIONAL_INFORMATION_TYPES.TEXT_AREA:
    case ADDITIONAL_INFORMATION_TYPES.CLAIM_COMMENT: {
      return (
        <FormGroup>
          <TextareaHookForm
            label={label}
            postLabel={postLabel}
            name={name}
            errors={errors}
            warnings={warnings}
            isRequired={isRequired}
            autoHeight
            onBlur={async () => {
              await onAdditionalInformationSave();
            }}
            register={register}
            isScrollIntoView={isScrollIntoView}
            dataTestId={`${
              ADDITIONAL_INFORMATION_TYPES.TEXT_AREA === type
                ? 'additional-information-input-textArea-'
                : 'additional-information-input-claimComment-'
            }-${id}`}
            internalId={id}
            suffixOptionButton={suffixOptionButton}
          />
        </FormGroup>
      );
    }

    case ADDITIONAL_INFORMATION_TYPES.CHECKBOX: {
      return (
        <FormGroup>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <Checkbox
                name={field.name}
                label={label}
                errors={errors}
                onChange={field.onChange}
                checked={field.value}
                isRequired={isRequired}
              />
            )}
          />
        </FormGroup>
      );
    }

    case ADDITIONAL_INFORMATION_TYPES.SERIAL_NUMBER: {
      return (
        <FormGroup>
          <AdditionalInformationSerialNumber
            name={name}
            label={label}
            neededInformationItem={neededInformationItem}
            postLabel={postLabel}
            placeholder={placeholder}
          />
        </FormGroup>
      );
    }
    case ADDITIONAL_INFORMATION_TYPES.NOTIFICATION_INFO:
    case ADDITIONAL_INFORMATION_TYPES.NOTIFICATION_ALERT: {
      const additionalInformationDidactics = [neededInformationItem].reduce(
        (acc, item) => {
          return {
            ...acc,
            [item.id]: item,
          };
        },
        {},
      );

      return (
        <AdditionalInformationDidactics
          additionalInformationDidactics={additionalInformationDidactics}
          isLoading={false}
        />
      );
    }
    case ADDITIONAL_INFORMATION_TYPES.SUPPLIER_CREDIT_NOTE_NUMBER:
    default: {
      return (
        <FormGroup>
          <InputHookForm
            internalId={id}
            errors={errors}
            warnings={warnings}
            isRequired={isRequired}
            placeholder={placeholder}
            label={label}
            name={name}
            postLabel={postLabel}
            onBlur={async () => {
              await onAdditionalInformationSave();
            }}
            dataTestId={`additional-information-input-input-${id}`}
            register={register}
            trigger={trigger}
            isScrollIntoView={isScrollIntoView}
            validatingRegex={validatingRegex}
            suffixOptionButton={suffixOptionButton}
            isLabelMultiLineAllowed
          />
        </FormGroup>
      );
    }
  }
};
